import { z } from 'zod';
/**
 * Die Schritte einer Fahrt in Wirkia Drivers (KON-245). Der Fahrer setzt sie selbst, in dieser Reihenfolge und jeden nur einmal. Mit jedem Schritt hält Wirkia die Position des Handys fest, das
 * Protokoll sehen nur Büro und Disposition im Backoffice.
 */
export const RIDE_STEPS = ['started', 'arrived', 'met', 'onboard', 'completed'];
/** Bezeichnung im Protokoll (Vergangenheit) */
export const RIDE_STEP_LABELS = {
    started: 'Fahrt gestartet, unterwegs zum Abholort',
    arrived: 'Am Abholort angekommen',
    met: 'Fahrgast angetroffen',
    onboard: 'Fahrgast im Fahrzeug',
    completed: 'Fahrt abgeschlossen',
};
/** Bezeichnung der Schaltfläche, die den Schritt setzt */
export const RIDE_STEP_ACTIONS = {
    started: 'Start',
    arrived: 'Angekommen',
    met: 'Fahrgast angetroffen',
    onboard: 'Fahrgast im Fahrzeug',
    completed: 'Fahrt abschließen',
};
/** Kurzform für Schilder in Listen */
export const RIDE_STEP_SHORT = {
    started: 'Fahrt zum Abholort',
    arrived: 'Am Abholort',
    met: 'Fahrgast angetroffen',
    onboard: 'Fahrgast an Bord',
    completed: 'Abgeschlossen',
};
/** Der Schritt, der als Nächster ansteht, null wenn alle gesetzt sind */
export function nextRideStep(done) {
    return RIDE_STEPS[done.length] ?? null;
}
/** Position des Handys. `recordedAt` ist die Zeit am Handy, der Server hält zusätzlich seine eigene fest. */
export const ridePositionInput = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy: z.number().min(0).max(100_000).nullable().optional(),
    speed: z.number().min(0).max(500).nullable().optional(),
    recordedAt: z.iso.datetime({ offset: true }).optional(),
});
export const rideStepInput = z
    .object({
    step: z.enum(RIDE_STEPS, 'Unbekannter Schritt'),
    /** Fehlt die Position (Standort aus, kein Empfang), wird der Schritt trotzdem gesetzt und das Protokoll vermerkt „ohne Position“ */
    position: ridePositionInput.nullable().optional(),
    /** Kilometerstand laut Tacho: Der Fahrer bestätigt oder korrigiert ihn vor dem Start, ohne ihn ist der Start nicht möglich (KON-251). Bei den übrigen Schritten entfällt er. */
    odometerKm: z.number('Bitte geben Sie den Kilometerstand als Zahl ein').int('Bitte geben Sie ganze Kilometer ein').min(0, 'Der Kilometerstand darf nicht negativ sein').max(3_000_000, 'Der Kilometerstand ist zu hoch').optional(),
    confirmWarnings: z.boolean().default(false),
    /** Nur beim Abschluss: Der Fahrer meldet Änderungen an der Fahrt (zusätzliche Kilometer, Wartezeit). Dann entsteht keine automatische Rechnung, das Büro prüft sie zuerst. Ohne Angabe gilt: keine Änderungen. */
    changes: z.boolean().optional(),
})
    .strict();
/** Positionen während der Fahrt, gesammelt in einer Anfrage */
export const rideTrackInput = z.object({ points: z.array(ridePositionInput).min(1, 'Keine Positionen').max(200, 'Höchstens 200 Positionen je Anfrage') }).strict();
const EARTH_KM = 6371;
/** Entfernung zweier Punkte in Kilometern (Großkreis) */
export function distanceKm(a, b) {
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(b.latitude - a.latitude);
    const dLon = rad(b.longitude - a.longitude);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
    return EARTH_KM * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}
/**
 * Gefahrene Strecke aus den Positionen des Handys (Fahrtenbuch, KON-251). Die Punkte liegen in zeitlicher Reihenfolge. Ungenaue Punkte (über 75 m) und kleine Sprünge unter 8 m zählen nicht,
 * weil das Rauschen des Empfängers im Stand sonst Kilometer erzeugt. Ein Sprung, der eine Geschwindigkeit über 70 m/s (252 km/h) ergäbe, gilt als Ausreißer. Ergebnis in Metern, null ohne
 * mindestens zwei brauchbare Punkte.
 */
export function trackDistanceMeters(points) {
    const usable = points.filter((p) => p.accuracyM == null || p.accuracyM <= 75);
    if (usable.length < 2)
        return null;
    let meters = 0;
    let last = usable[0];
    for (let i = 1; i < usable.length; i++) {
        const p = usable[i];
        const step = distanceKm(last, p) * 1000;
        if (step < 8)
            continue;
        const seconds = (new Date(p.recordedAt).getTime() - new Date(last.recordedAt).getTime()) / 1000;
        if (seconds > 0 && step / seconds > 70)
            continue;
        meters += step;
        last = p;
    }
    return Math.round(meters);
}
//# sourceMappingURL=ride-steps.js.map